import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import categoryAPI from "../../api/categoryApi";
import { useParams } from "react-router-dom";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import CategoryCard from "../categories/Card";
import ProductCard from "../products/Card";
import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "../homepage/Shared/CustomSwiper";

function DetailPage() {
  const { "*": slug } = useParams();
  const [item, setItem] = useState({});
  const [subcategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState("");

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const observer = useRef();

  const fetchCategoryDetail = async (currentPage = 1, reset = false) => {
    setLoading(true);

    try {
      const res = await categoryAPI.details(slug, { page: currentPage });

      if (res.code === 200) {
        const { category, subcategories, products, pagination } = res.data;

        setItem(category);
        setSubCategories(subcategories);
        setMetaData(category?.meta);

        const newProducts = products || [];

        setProducts((prev) =>
          reset ? newProducts : [...prev, ...newProducts],
        );

        setTotalCount(pagination?.total || 0);
      }
    } catch (err) {
      console.error("Error loading blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setProducts([]);
    setPage(1);

    fetchCategoryDetail(1, true);
  }, [slug]);

  useEffect(() => {
    if (page === 1) return;

    fetchCategoryDetail(page);
  }, [page]);

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && products.length < totalCount) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, products, totalCount],
  );

  return (
    <>
      <Breadcrump title={item?.name} />

      <SEO meta={metaData} />

      {subcategories && subcategories.length > 0 && (
        <section className="subcategory-section py-5">
          <div className="container">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              slidesPerView={1}
              spaceBetween={20}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000 }}
              breakpoints={{
                0: { slidesPerView: 1.8 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 },
              }}
            >
              {subcategories.map((subcategory, index) => (
                <SwiperSlide key={index}>
                  <CategoryCard item={subcategory} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {products && products.length > 0 && (
        <div className="py-4 bg-white">
          <div className="container">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className=" text-start">
                <h4
                  className="section-title"
                 
                >
                  Our Products
                </h4>
              </div>
              <div className="row g-3 mt-2">
                <Suspense fallback={<div></div>}>
                  {products.map((product, index) => {
                    if (index === products.length - 1) {
                      return (
                        <div
                          className="col-lg-3 col-12 mb-2"
                          key={product._id || index}
                          ref={lastProductRef}
                        >
                          <ProductCard item={product} />
                        </div>
                      );
                    }

                    return (
                      <div
                        className="col-lg-3 col-12 mb-2"
                        key={product._id || index}
                      >
                        <ProductCard item={product} />
                      </div>
                    );
                  })}
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-4">
          <p>Loading more products...</p>
        </div>
      )}

      {!loading && subcategories.length === 0 && products.length === 0 && (
        <div className="py-5 bg-white">
          <div className="container">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-center">
                <img
                  src={`${process.env.PUBLIC_URL}/images/blank_image.png`}
                  alt="No subcategories"
                  className="w-50 mb-3"
                />

                <p>No Product Found in {item?.name} category</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && products.length > 0 && products.length >= totalCount && (
        <div className="text-center py-4">
          <p>✨ You’ve reached the end</p>
        </div>
      )}
    </>
  );
}

export default DetailPage;
